/**
 * CJIS-compliant System Use Notification banner.
 *
 * Per FBI CJIS Security Policy v5.9.5 §5.5.2 (and 2013-onward practice),
 * any system that accesses Criminal Justice Information must:
 *   - DISPLAY a notification before granting access, AND
 *   - REQUIRE the user to ACKNOWLEDGE and take an EXPLICIT ACTION to proceed.
 *
 * Disclosure content:
 *   - identifies the system as containing CJI / authorized-use-only
 *   - warns that unauthorized access may result in criminal/civil penalties
 *     (CFAA — 18 U.S.C. § 1030)
 *   - states that activity is monitored, logged, and subject to audit
 *   - states that use constitutes consent to monitoring
 *   - notes that information may be disclosed as required by law
 *
 * Three display modes:
 *   - `variant="banner"` — passive block; signing in implies consent (legacy)
 *   - `variant="banner-with-ack"` — banner + checkbox; gates submit upstream
 *   - `variant="footer"` — condensed attestation below the form
 *
 * The acknowledgment state lives in the parent form so it can wire into
 * both the submit-button-disabled logic and the sign-in audit event.
 */

import { Callout, Checkbox, Icon, Intent } from "@blueprintjs/core";

type BannerVariant = "banner" | "banner-with-ack" | "footer";

interface Props {
  variant?: BannerVariant;
  /** Required when variant === "banner-with-ack". Mirrors checkbox state. */
  acknowledged?: boolean;
  /** Required when variant === "banner-with-ack". Fires on checkbox toggle. */
  onAcknowledge?: (next: boolean) => void;
}

export function SystemUseNotification({
  variant = "banner",
  acknowledged = false,
  onAcknowledge,
}: Props) {
  if (variant === "footer") {
    return (
      <div style={footerStyle}>
        <Icon icon="shield" size={11} /> This system contains Criminal Justice
        Information. All activity is monitored and recorded; misuse may result
        in criminal or administrative penalties.
      </div>
    );
  }

  const requireAck = variant === "banner-with-ack";

  return (
    <Callout
      intent={Intent.WARNING}
      icon="warning-sign"
      title="Authorized access only"
      style={{ marginTop: 16, marginBottom: 0 }}
    >
      <div style={bannerBodyStyle}>
        <p style={paragraphStyle}>
          This system contains <strong>Criminal Justice Information (CJI)</strong>{" "}
          protected under FBI CJIS Security Policy. Unauthorized access, use,
          modification, or disclosure of information contained in or accessed
          through this system is prohibited and may result in administrative,
          civil, and/or criminal penalties under federal and state law
          including the Computer Fraud and Abuse Act (18 U.S.C. § 1030).
        </p>
        <p style={paragraphStyle}>
          You acknowledge that:
        </p>
        <ul style={listStyle}>
          <li>You are an authorized user with a valid law-enforcement purpose.</li>
          <li>All activity is monitored, logged, and subject to audit.</li>
          <li>Your use of this system constitutes consent to such monitoring.</li>
          <li>Information may be disclosed as required by law.</li>
          <li>You will protect information from unauthorized disclosure.</li>
        </ul>
        {requireAck && (
          <div style={ackRowStyle}>
            <Checkbox
              checked={acknowledged}
              onChange={(e) =>
                onAcknowledge?.((e.currentTarget as HTMLInputElement).checked)
              }
              labelElement={
                <span style={ackLabelStyle}>
                  I have read and acknowledge the above. I will use this system
                  only for authorized law-enforcement purposes.
                </span>
              }
              data-testid="cjis-acknowledge"
            />
          </div>
        )}
      </div>
    </Callout>
  );
}

const bannerBodyStyle: React.CSSProperties = {
  fontSize: 12,
  lineHeight: 1.45,
};

const paragraphStyle: React.CSSProperties = {
  margin: "4px 0 6px",
};

const listStyle: React.CSSProperties = {
  margin: "0 0 8px",
  paddingLeft: 18,
};

const ackRowStyle: React.CSSProperties = {
  marginTop: 8,
  paddingTop: 8,
  borderTop: "1px solid var(--bp-border-soft)",
};

const ackLabelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
};

const footerStyle: React.CSSProperties = {
  marginTop: 18,
  // 10px was below the practical floor for body copy — this is a CJIS
  // monitoring-and-consent attestation, so it has to be readable to be
  // meaningful. 12px matches the other secondary copy on the auth card.
  fontSize: 12,
  color: "var(--bp-text-dim)",
  display: "flex",
  alignItems: "flex-start",
  gap: 6,
  lineHeight: 1.5,
};
